<template>
  <div class="bdx-manager-list">
    <AdvancedScrollList :items="items" :loading="isFetching">
      <template v-slot:header>
        <BDXMatchPendingManagerListHeader>
          <template v-slot:first>
            <b-form-checkbox
              v-if="!!items.length"
              v-model="selectAll"
              :disabled="isFetching"
            ></b-form-checkbox>
          </template>
        </BDXMatchPendingManagerListHeader>
      </template>

      <template v-slot:item="{ item }">
        <BDXMatchPendingManagerListItem
          :item="item"
          :class="{
            'bg-secondary font-weight-bold': $props.activeItems.includes(
              item.id
            )
          }"
        >
          <template v-slot:preview>
            <BDXPreviewPopover
              :item="item"
              :fetch-fn="getBdxPreviewData"
              :columns="MATCH_PREVIEW_COLUMNS"
            />
            <a
              :disabled="!!modelValue.length"
              :style="{ opacity: !!modelValue.length ? 0.5 : 1 }"
              role="button"
              @click.prevent="() => $emit('edit:item', item)"
            >
              <BaseIcon icon="pencil" tag="i" :font-scale="0.9" />
            </a>
          </template>

          <template v-slot:first>
            <BaseCheckbox v-model="modelValue" :checked-value="item.id" />
          </template>
        </BDXMatchPendingManagerListItem>
      </template>

      <template v-slot:empty-message>No UMRs found.</template>
    </AdvancedScrollList>

    <BDXMatchPendingManagerListPagination
      v-model="pagination"
      :total="totalPages"
      @input="fetchItems"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  watch,
  inject,
  onMounted
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { Fragment } from 'vue-fragment'

import { extractBdxRows } from '@util'
import { useTryCatch, useCurrentUser, useUserCompany } from '@composables'
import { LincolnNewGlobalUMRService } from '@services'

import { BaseCheckbox, BaseIcon } from '@components/base'
import { AdvancedScrollList } from '@components/advanced'

import { BDXMatchPendingManagerInjectionKey } from './BDXMatchPendingManager.constants'

const MATCH_PREVIEW_COLUMNS = [
  { key: 'TPA_Name',                    label: 'TPA' },
  { key: 'Coverholder_Name',            label: 'Coverholder' },
  { key: 'Unique_Market_Reference_UMR', label: 'UMR' },
  { key: 'Risk_Code',                   label: 'Risk Code' },
  { key: 'Lloyds_Cat_Code',             label: 'Cat Code' },
  { key: 'Section_No',                  label: 'Section #' },
  { key: 'Settlement_Currency',         label: 'Settlement Currency' },
  { key: 'Year_of_Account',             label: 'YOA' },
  { key: 'Binder_Contract_Inception',   label: 'Binder Inception' }
]

import BDXMatchPendingManagerListHeader from './BDXMatchPendingManagerListHeader.vue'
import BDXMatchPendingManagerListItem from './BDXMatchPendingManagerListItem.vue'
import BDXMatchPendingManagerListPagination from './BDXMatchPendingManagerListPagination.vue'
import BDXPreviewPopover from '../BDXPreviewPopover.vue'

export default defineComponent({
  name: 'BDXMatchPendingManagerList',
  components: {
    Fragment,

    BaseCheckbox,
    BaseIcon,

    AdvancedScrollList,

    BDXMatchPendingManagerListHeader,
    BDXMatchPendingManagerListItem,
    BDXMatchPendingManagerListPagination,
    BDXPreviewPopover
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    activeItems: {
      type: Array,
      default: () => []
    }
  },
  emits: ['input', 'edit:item'],
  setup(props, { emit }) {
    const { useGetters } = createVuexHelpers()

    const { bdxFilters } = useGetters(['bdxFilters'])

    const { isSuperUser } = useCurrentUser()
    const { id: userCompanyId } = useUserCompany()

    const { search, columnFilters } = inject(BDXMatchPendingManagerInjectionKey)

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    // Data fetching
    const isFetching = ref(false)

    const pagination = ref({
      page: 1,
      page_size: 100
    })
    const totalPages = ref(0)

    const items = ref([])

    const fetchItems = async () => {
      isFetching.value = true

      selectAll.value = false

      await useTryCatch(async () => {
        const companyId = isSuperUser.value
          ? bdxFilters.value.companyId
          : userCompanyId.value

        const { data } = await LincolnNewGlobalUMRService.list(companyId, {
          search: search.value,
          ...columnFilters.value,
          page: pagination.value.page,
          page_size: pagination.value.page_size
        })
        const { count, results } = data || {}

        items.value = results

        totalPages.value = count
      })

      isFetching.value = false
    }

    watch([search, columnFilters, bdxFilters], fetchItems, {
      deep: true
    })

    onMounted(fetchItems)

    onMounted(() => {
      Vue.prototype.$emitter.on('bdx-edit-form:success', async ({ status }) => {
        if (!status) return

        await fetchItems()
      })

      Vue.prototype.$emitter.on('files-uploaded', async ({ type, success }) => {
        if (!success || 'bdx' !== type) return

        await fetchItems()
      })
    })

    // Bulk selection
    const selectAll = ref(false)

    watch(selectAll, (newSelectAll) => {
      if (!newSelectAll) {
        emit('input', [])
      } else {
        const ids = items.value.map((item) => item.id)

        emit('input', ids)
      }
    })

    const getBdxPreviewData = async (item) => {
      if (!item.umr_rc_cc_sn) return []

      const response = await LincolnNewGlobalUMRService.getBdxDataRows({
        umr_rc_cc_sn: item.umr_rc_cc_sn
      })
      return extractBdxRows(response.data)
    }

    return {
      MATCH_PREVIEW_COLUMNS,

      modelValue,

      isFetching,

      pagination,
      totalPages,

      items,
      fetchItems,

      selectAll,

      getBdxPreviewData
    }
  }
})
</script>

<style scoped>

@media screen and (max-width: 768px){
  .bdx-manager-list > div {
    overflow: auto;
  }
  .bdx-manager-list >>> .list-group {
    min-width: 700px;
  }
  
}
.bdx-manager-list >>> .preview-col {
  width: 44px;
  min-width: 44px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.bdx-manager-list >>> .bdx-col {
  padding: 0 8px;
}
.bdx-manager-list >>> .bdx-col.rc-col {
  width: 100px;
}
.bdx-manager-list >>> .bdx-col.cc-col {
  width: 160px;
}
.bdx-manager-list >>> .bdx-col.sn-col {
  width: 160px;
}
</style>

